import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

const schema = z.object({
  roof_age_years:       z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(0).max(200).nullable()),
  roof_last_inspection: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  roof_warranty_expiry: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  has_skylights:        z.boolean().optional(),
  gutter_guards:        z.boolean().optional(),
  notes:                z.string().max(1000).trim().nullable().optional(),
});
type FormData = z.infer<typeof schema>;

function roofCondition(ageYears: number | null): { label: string; color: string; bg: string } {
  if (ageYears === null) return { label: 'Unknown', color: '#6b7280', bg: '#f9fafb' };
  if (ageYears <= 5)  return { label: 'New', color: '#15803d', bg: '#f0fdf4' };
  if (ageYears <= 10) return { label: 'Good', color: '#0369a1', bg: '#f0f9ff' };
  if (ageYears <= 15) return { label: 'Aging', color: '#b45309', bg: '#fffbeb' };
  if (ageYears <= 20) return { label: 'Near end of life', color: '#c2410c', bg: '#fff7ed' };
  return { label: 'Past lifespan', color: '#b91c1c', bg: '#fef2f2' };
}

export function RoofProfile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const estimatedAge = home?.year_built
    ? CURRENT_YEAR - home.year_built
    : null;

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      roof_age_years:       home?.roof_age_years ?? null,
      roof_last_inspection: home?.roof_last_inspection ?? null,
      roof_warranty_expiry: home?.roof_warranty_expiry ?? null,
      has_skylights:        home?.has_skylights ?? false,
      gutter_guards:        home?.gutter_guards ?? false,
      notes:                null,
    },
  });

  const hasSkylights = watch('has_skylights');
  const gutterGuards = watch('gutter_guards');
  const roofAge      = watch('roof_age_years');

  const condition = roofCondition(roofAge ?? null);

  const mutation = useMutation({
    mutationFn: (data: FormData) => api.put<Home>(`/homes/${home!.id}`, {
      roof_age_years:       data.roof_age_years,
      roof_last_inspection: data.roof_last_inspection ?? null,
      roof_warranty_expiry: data.roof_warranty_expiry ?? null,
      has_skylights:        data.has_skylights ?? false,
      gutter_guards:        data.gutter_guards ?? false,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homes'] });
      navigate('/app/profile');
    },
  });

  if (!home) return null;

  const onSubmit = (data: FormData) => mutation.mutate(data);

  function Toggle({ name, checked, label, hint }: {
    name: 'has_skylights' | 'gutter_guards';
    checked: boolean | undefined;
    label: string;
    hint: string;
  }) {
    return (
      <label style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 0', cursor: 'pointer',
        borderBottom: '1px solid #f3f4f6',
      }}>
        <div>
          <p style={{ margin: 0, fontSize: 15, color: '#111827', fontWeight: 500 }}>{label}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>{hint}</p>
        </div>
        <div style={{ position: 'relative', flexShrink: 0, marginLeft: 12 }}>
          <input
            {...register(name)}
            type="checkbox"
            style={{ opacity: 0, width: 44, height: 24, position: 'absolute', top: 0, left: 0, cursor: 'pointer', margin: 0, zIndex: 1 }}
          />
          <div style={{ width: 44, height: 24, borderRadius: 12, background: checked ? '#1D4ED8' : '#d1d5db', transition: 'background 0.15s', pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.15s' }} />
          </div>
        </div>
      </label>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Roof Profile</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
          Track roof age, inspections, and warranty
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Condition badge */}
        {(roofAge !== null || estimatedAge !== null) && (
          <div style={{
            padding: '14px 16px', background: condition.bg, borderRadius: 12,
            border: `1.5px solid ${condition.color}30`,
          }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: condition.color }}>
              Roof condition: {condition.label}
            </p>
            {roofAge !== null && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: condition.color }}>
                {roofAge} years old · {Math.max(0, 25 - roofAge)} years estimated remaining
              </p>
            )}
            {roofAge === null && estimatedAge !== null && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                Based on home age (~{estimatedAge} years) — set roof age below if replaced
              </p>
            )}
          </div>
        )}

        {/* Roof age */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
            Roof age (years)
          </label>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#6b7280' }}>
            If your roof was replaced after the home was built, enter the age of the current roof.
            {estimatedAge !== null && ` Leave blank to use estimated home age (${estimatedAge} years).`}
          </p>
          <input
            {...register('roof_age_years')}
            type="number"
            min={0}
            max={200}
            placeholder={estimatedAge !== null ? `Estimated: ${estimatedAge} years` : 'e.g. 12'}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Last inspection */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
            Last professional inspection
          </label>
          <input
            {...register('roof_last_inspection')}
            type="date"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Warranty expiry */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
            Warranty expiry date
          </label>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#6b7280' }}>
            Most shingle warranties are 25-50 years (material) and 5-10 years (workmanship).
          </p>
          <input
            {...register('roof_warranty_expiry')}
            type="date"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Features */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 15, color: '#111827' }}>Roof features</p>
          <Toggle
            name="has_skylights"
            checked={hasSkylights}
            label="Has skylights"
            hint="Skylights require separate seal inspection every 6 months"
          />
          <Toggle
            name="gutter_guards"
            checked={gutterGuards}
            label="Has gutter guards"
            hint="Gutter guards reduce cleaning frequency but need annual inspection"
          />
        </div>

        {/* Inspection tip */}
        {(roofAge ?? estimatedAge ?? 0) > 15 && (
          <div style={{
            padding: '14px 16px', background: '#fef2f2', borderRadius: 12,
            border: '1.5px solid #fecaca',
          }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#b91c1c' }}>
              Roof inspection recommended
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#991b1b' }}>
              Roofs over 15 years old should be inspected twice per year. Get 3 replacement quotes now while the roof is functional — emergency replacement costs 15-30% more.
            </p>
          </div>
        )}

        {mutation.error instanceof Error && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#ef4444', fontSize: 14 }}>
            Failed to save. Please try again.
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          style={{
            padding: '14px', background: '#1D4ED8', color: '#fff', border: 'none',
            borderRadius: 12, fontWeight: 600, fontSize: 16, cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting || mutation.isPending ? 'Saving…' : 'Save roof profile'}
        </button>
      </form>

      <NavBar />
    </div>
  );
}
